// FILE: /src/Components/Apps/AppHistoryItem.js

import React from "react";
import { Button, Image, Tag, Tooltip } from "antd";
import moment from "moment";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

const statusColorMap = {
  processing: "blue",
  completed: "green",
  failed: "red",
  refunded: "orange",
};

const AppHistoryItem = ({ transaction, groups = [], onOpenTransaction }) => {
  const groupName =
    groups.find((group) => String(group._id) === String(transaction.groupId))?.name ||
    "Brand";

  const status = transaction.status || "processing";
  const statusColor = statusColorMap[status] || "default";

  const inputUrl =
    transaction.inputAssetUrl ||
    transaction.input?.imageUrl ||
    transaction.input?.url ||
    transaction.input?.savedInputUrls?.[0] ||
    null;

  const outputUrl =
    transaction.outputAssetUrl ||
    transaction.output?.url ||
    null;

  const isCaptionTransaction =
    String(transaction.appId || "").startsWith("caption.") ||
    Array.isArray(transaction.output?.captions) ||
    Array.isArray(transaction.output?.variants);

  const captionOutputs =
    transaction.output?.captions ||
    transaction.output?.variants ||
    [];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:shadow-md transition">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex gap-3 flex-grow">
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-wand-magic-sparkles"></i>
          </div>

          <div className="min-w-0 flex-grow">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-bold text-gray-900 mb-0">
                {transaction.appName || transaction.appId}
              </h3>

              <Tag color={statusColor} className="m-0 capitalize">
                {status}
              </Tag>

              <Tag className="m-0">
                {transaction.creditsCharged || 0} credit
                {Number(transaction.creditsCharged || 0) === 1 ? "" : "s"}
              </Tag>

              {Number(transaction.creditsRefunded || 0) > 0 && (
                <Tag color="orange" className="m-0">
                  {transaction.creditsRefunded} refunded
                </Tag>
              )}
            </div>

            <div className="text-sm text-gray-500 mt-1">
              <span>{groupName}</span>
              <span className="mx-2">•</span>
              <span>{moment(transaction.createdAt).format("MMM D, YYYY h:mm A")}</span>
            </div>

            {isCaptionTransaction && captionOutputs.length > 0 && (
              <div className="mt-3 space-y-2">
                {captionOutputs.slice(0, 2).map((captionItem, index) => {
                  const captionText =
                    typeof captionItem === "string"
                      ? captionItem
                      : captionItem.caption || captionItem.text || "";

                  return (
                    <div
                      key={`${captionText}_${index}`}
                      className="bg-gray-50 border border-gray-100 rounded-xl p-3"
                    >
                      <p className="text-sm text-gray-700 line-clamp-3 mb-0 whitespace-pre-wrap">
                        {captionText || "No caption text returned."}
                      </p>
                    </div>
                  );
                })}

                {captionOutputs.length > 2 && (
                  <p className="text-xs text-gray-400 mb-0">
                    +{captionOutputs.length - 2} more caption
                    {captionOutputs.length - 2 === 1 ? "" : "s"}
                  </p>
                )}
              </div>
            )}

            {transaction.errorMessage && (
              <Tooltip title={transaction.errorMessage}>
                <p className="text-sm text-red-600 mt-2 line-clamp-2">
                  {transaction.errorMessage}
                </p>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {inputUrl && (
              <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden border">
                <Image
                  src={inputUrl}
                  alt="Input"
                  width={64}
                  height={64}
                  className="object-cover"
                />
              </div>
            )}

            {outputUrl && (
              <div className="w-16 h-16 rounded-lg bg-gray-100 overflow-hidden border">
                <Image
                  src={outputUrl}
                  alt="Output"
                  width={64}
                  height={64}
                  className="object-cover"
                />
              </div>
            )}
          </div>

                    <div className="flex flex-col gap-2">
            {outputUrl && (
              <Button
                size="small"
                href={outputUrl}
                target="_blank"
                icon={<DownloadOutlined />}
              >
                Download
              </Button>
            )}

            {isCaptionTransaction && captionOutputs.length > 0 && (
              <Button
                size="small"
                onClick={() => {
                  const textToCopy = captionOutputs
                    .map((item) =>
                      typeof item === "string"
                        ? item
                        : item.caption || item.text || ""
                    )
                    .filter(Boolean)
                    .join("\n\n");

                  navigator.clipboard.writeText(textToCopy);
                }}
              >
                Copy
              </Button>
            )}

            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => onOpenTransaction?.(transaction)}
            >
              Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppHistoryItem;